// Product Stats
export interface ProductStatsData {
  id: number;
  codigo: string;
  nombre?: string;
  sku?: string;
  color?: string;
  categoria?: string;
  grupo?: string;
  stock: number;
  total_quantity?: number;
}
// Un permiso de lista de precios que este usuario tiene asignado, con el
// tope de descuento configurado para esa lista (CLAUDE.md §4/§8.11). Vista
// de lectura tal como viene en `User.price_list_assignments` (GET /users,
// /users/{id}). Se edita desde Gestión de Usuarios (movido 2026-09-04 desde
// el módulo de Listas de Precios) - ver UserPriceListAssignmentInput para
// la forma que espera la escritura.
export interface UserPriceListAssignment {
  price_list_id: number;
  price_list_name: string;
  max_discount_pct?: number | null; // Puntos porcentuales (15 = 15%), no fracción
}

// Lo que espera PUT /users/{id}/price-list-assignments - un item por lista
// que este usuario debería poder cotizar, con su propio tope de descuento
// (CLAUDE.md §4/§8.11). Contraparte "para escribir" de UserPriceListAssignment
// de arriba (esa forma trae price_list_name, de solo lectura; esta no la
// necesita porque el caller ya sabe qué lista está asignando).
export interface UserPriceListAssignmentInput {
  price_list_id: number;
  max_discount_pct?: number | null;
}

// Tipos de usuario y autenticación
export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  telefono?: string;
  erp_vendedor_number?: string;
  // Approved section discounts for Mano de Obra and Flete (CLAUDE.md §8.7,
  // 2026-09-08). Percentage points (e.g. 15 = 15%); null/undefined = no cap.
  // Materiales' approved discount stays per-price-list in price_list_assignments.
  mo_max_discount_pct?: number | null;
  flete_max_discount_pct?: number | null;
  price_list_assignments: UserPriceListAssignment[];
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface GoogleLoginRequest {
  id_token: string;
}

// Phase 1B (CLAUDE.md §1/§2): the rare (~1-2 people) dual-account case -
// POST /auth/google returns this instead of a token when the authenticated
// email is found in both AR's and UY's databases.
export interface GoogleCountrySelectionOption {
  country: string; // "AR" | "UY"
  name: string;
}

export interface GoogleCountrySelectionRequired {
  requires_country_selection: true;
  options: GoogleCountrySelectionOption[];
}

export interface GoogleCountrySelectionRequest {
  id_token: string;
  country: string;
}

export function isCountrySelectionRequired(
  result: TokenResponse | GoogleCountrySelectionRequired
): result is GoogleCountrySelectionRequired {
  return (result as GoogleCountrySelectionRequired).requires_country_selection === true;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  full_name: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  // Returns the country options when the account exists in both AR and UY
  // (dual-account pick screen) instead of logging in directly - the caller
  // (Login.tsx) then calls selectCountryAfterGoogleLogin with the choice.
  loginWithGoogle: (idToken: string) => Promise<GoogleCountrySelectionOption[] | void>;
  selectCountryAfterGoogleLogin: (idToken: string, country: string) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => void;
  // Re-fetches the logged-in user's own record from the server and updates
  // both the context state and localStorage['user'] together. Fixes the
  // "edited my own name in Gestión de Usuarios, still shows the old name"
  // bug - AuthContext otherwise only ever hydrates `user` once, at
  // login/mount, from localStorage. Call after any self-edit (see
  // UserManagement.tsx's save handler).
  refreshUser: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

// ml_operator: solo facturación Mercado Libre (/ml).
export type UserRole = 'admin' | 'user' | 'ml_operator';

// Tipos para gestión de usuarios (admin)
export interface UserCreateRequest {
  email: string;
  name: string;
  password?: string; // opcional: usuarios SSO no tienen contraseña
  role: UserRole;
  telefono?: string;
  erp_vendedor_number?: string;
  mo_max_discount_pct?: number | null;
  flete_max_discount_pct?: number | null;
}

export interface UserUpdateRequest {
  email?: string;
  name?: string;
  role?: UserRole;
  is_active?: boolean;
  telefono?: string;
  erp_vendedor_number?: string;
  mo_max_discount_pct?: number | null;
  flete_max_discount_pct?: number | null;
}

export interface UserFilters {
  email?: string;
  role?: string;
  is_active?: boolean;
  skip?: number;
  limit?: number;
}

// Tipos de productos
//
// CLAUDE.md §4 (2026-09-02 "Product/pricing separation"): precio_unitario,
// currency y descuento se fueron de Product por completo - el precio de
// Materiales vive únicamente en PriceListEntry (ver más abajo). `stock` es
// de solo lectura acá (viene de la tabla ProductStock, CLAUDE.md §7) - se
// edita vía apiService.products.updateStock, no en este formulario.
// `factor_conversion_m2` (renombrado de `precio_m2` 2026-09-04, confirmado
// por el Product Owner) se mantiene: pese al nombre no es un precio, es un
// factor de conversión unidad->m2 (CLAUDE.md §7).
export interface Product {
  id: number;
  sku?: string;
  codigo: string;
  color?: string;
  grupo?: string;
  categoria?: string;
  descripcion?: string;
  proveedor?: string;
  unidad?: string;
  peso?: number;
  moq_m2?: number;
  stock?: number;  // Solo lectura - ver ProductStock
  largo?: number;
  ancho?: number;
  espesor?: number;
  ancho_util?: number;
  factor_conversion_m2?: number;  // NO es un precio - factor de conversión unidad->m2
  estructura_m2?: string;  // Lista de productos: "codigo,cantidad\ncodigo,cantidad"
  ah?: string;  // 'A' | 'H' - clasificación ERP (CLAUDE.md §8.5/§8.6)
  is_active: boolean;
  is_outlet?: boolean;
  foto_url?: string;  // Foto en Cloud Storage (Phase 5, CLAUDE.md §7) - compartida entre países
  created_at: string;
  updated_at?: string;
}

export interface ProductFormData {
  sku: string;
  codigo: string;
  color?: string;
  grupo?: string;
  categoria?: string;
  descripcion?: string;
  proveedor?: string;
  unidad?: string;
  peso?: number;
  moq_m2?: number;
  largo?: number;
  ancho?: number;
  espesor?: number;
  ancho_util?: number;
  factor_conversion_m2?: number;
  estructura_m2?: string;  // Lista de productos: "codigo,cantidad\ncodigo,cantidad"
  ah?: string;
  is_active?: boolean;
  is_outlet?: boolean;
}

// CLAUDE.md §7: catálogo vía CSV/XLS, reemplaza el sync en vivo con Google
// Sheets retirado para Materiales.
export interface ProductImportResult {
  created: number;
  updated: number;
  errors: string[];
}

export interface ProductFilters {
  search?: string;
  categoria?: string;
  grupo?: string;
  color?: string;
  is_active?: boolean;
  active_only?: boolean;
  inactive_only?: boolean;
  outlet_only?: boolean;
  // Worker brief "catalog-list-ui-pattern" (2026-09-05): la única forma de
  // pedir todos los estados (activo+inactivo+outlet) en una sola llamada -
  // ninguna combinación de los otros tres booleanos lo permite (ver
  // product_service.py's list_products). La necesita el filtro de columna
  // "Estado" de ProductList.tsx, que filtra client-side.
  all_statuses?: boolean;
  limit?: number;  // Límite de productos a mostrar
}

// Un usuario asignado a la lista, con su tope de descuento (CLAUDE.md
// §8.11) - puntos porcentuales (15 = 15%), no la convención de fracción de
// PriceListEntry.descuento (esta cota no tiene relación con ese campo).
// **Solo lectura** (movido 2026-09-04): esta forma alimenta la columna
// "Usuarios Asignados" de solo lectura en Listas de Precios - la edición
// real vive en Gestión de Usuarios via UserPriceListAssignmentInput arriba.
export interface PriceListUserAssignment {
  user_id: number;
  name: string;
  email: string;
  max_discount_pct?: number;
}

// --- Listas de precios (Phase 4, CLAUDE.md §4) ---
export interface PriceList {
  id: number;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  assigned_user_ids: number[];
  assigned_users: PriceListUserAssignment[];
}

// CLAUDE.md §4 (corrected 2026-09-03): una entrada ya no es un rango
// [valid_from, valid_to] - un solo `effective_date` ("a partir de cuándo
// rige este precio"), y `descuento` (fracción, 0.10 = 10%) vive acá,
// por producto y por lista, no en Product.
export interface PriceListEntry {
  id: number;
  price_list_id: number;
  product_id: number;
  price: number;
  effective_date: string;  // ISO date (YYYY-MM-DD)
  descuento?: number;  // Fracción, ej. 0.10 = 10%
  created_at: string;
  updated_at?: string;
  product?: Product;
}

export interface PriceListEntryFormData {
  product_id: number;
  price: number;
  effective_date: string;
  descuento?: number;
}

// Respuesta de crear/actualizar una entrada: incluye los avisos pasivos de
// "este producto también tiene precio en otra(s) lista(s)" (CLAUDE.md §4).
export interface PriceListEntryResult {
  entry: PriceListEntry;
  cross_list_alerts: string[];
}

export interface PriceListImportResult {
  created: number;
  updated: number;
  errors: string[];
  cross_list_alerts: string[];
  // Active-product-coverage check (CLAUDE.md §4): active products with no
  // entry on this list at all after the upload.
  missing_active_products: string[];
}

// --- Fletes lookup (Phase 5, CLAUDE.md §6/§7 - capacity_tier replaced by
// structured peso_max/m2_max thresholds, 2026-09-03) ---
export interface FreightRate {
  id: number;
  zone: string;
  peso_max: number;
  m2_max: number;
  codigo?: string;
  descripcion?: string;
  price: number;
  currency: string;
  precio_vigente_desde?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface FreightRateFormData {
  zone: string;
  peso_max: number;
  m2_max: number;
  codigo?: string;
  descripcion?: string;
  price: number;
  currency?: string;
  precio_vigente_desde?: string;
  is_active?: boolean;
}

// --- Catalog import reconciliation reports (Phase 5, CLAUDE.md §7) ---
export interface CatalogImportRun {
  id: number;
  kind: 'materials' | 'mano_obra' | 'fletes' | 'photos';
  status: 'success' | 'partial' | 'error';
  summary: string;  // JSON-encoded report - parse as needed per `kind`
  started_at: string;
  finished_at?: string;
}

// Tipos de presupuestos - NUEVO MODELO

// Environment Materials
export interface EnvironmentMaterial {
  id: number;
  environment_id: number;
  product_id?: number;
  descripcion: string;
  codigo: string;
  sku?: string;
  foto?: string;
  unidades: number;
  metros2?: number;
  precio_unitario: number;
  observaciones?: string;
  monto: number;
  es_in_situ: boolean;
  currency: string;
  // Line discounts (CLAUDE.md §8.7 outlet model), fractions (0.10 == 10%).
  // `descuento_producto` is server-managed (captured at add-time, outlet
  // products only). `descuento_linea` is the applied discount: outlet lines
  // 0..descuento_producto, non-outlet lines filled from the ambiente %.
  descuento_producto?: number;
  descuento_linea?: number;
  con_desperdicio?: boolean;
  con_estructura?: boolean;
  created_at: string;
  updated_at?: string;
}

export interface EnvironmentMaterialCreate {
  product_id?: number;
  descripcion: string;
  codigo: string;
  sku?: string;
  foto?: string;
  unidades: number;
  metros2?: number;
  precio_unitario: number;
  observaciones?: string;
  monto: number;
  es_in_situ?: boolean;
  currency?: string;
  descuento_producto?: number;
  descuento_linea?: number;
  con_desperdicio?: boolean;
  con_estructura?: boolean;
}

// Environment Installation Materials
export interface EnvironmentInstallationMaterial {
  id: number;
  environment_installation_id: number;
  descripcion: string;
  precio_unitario: number;
  cantidad: number;
  subtotal: number;
  currency: string;
  created_at: string;
  updated_at?: string;
}

export interface EnvironmentInstallationMaterialCreate {
  descripcion: string;
  precio_unitario: number;
  cantidad: number;
  subtotal: number;
  currency?: string;
}

// Installation Service (catálogo de servicios) - one row per service
// *variant* (CLAUDE.md §7, decided 2026-08-30): Standard/Compleja are
// separate rows sharing the same `descripcion`, not a field. `codigo` is the
// stable per-variant identifier (matches the rebuilt Sheet's CODIGO column);
// `complejidad` is a display/filter label only, never used for pricing.
// `currency` restored 2026-09-01 (CLAUDE.md §8.5) - reverses Phase 6's
// USD-only decision; `precio` is in whichever currency this says ('USD' or
// 'ARS'), not USD always. `trabajo` retired 2026-09-03 (CLAUDE.md §7) -
// merged into `descripcion`, the sole name field now, required.
// `precio_vigente_desde` is new (VALIDODESDE in the real file) - a static
// "this price took effect on this date" field, not a history table.
// `precio === 0` means "Consultar" (price on request, CLAUDE.md §7), not a
// real free price - every display surface reading it renders that instead.
export interface InstallationService {
  id: number;
  codigo: string;
  descripcion: string;
  unidad: string;
  complejidad?: string;
  precio: number;
  currency: string;
  precio_vigente_desde?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface InstallationServiceCreate {
  codigo: string;
  descripcion: string;
  unidad: string;
  complejidad?: string;
  precio: number;
  currency?: string;
  precio_vigente_desde?: string;
  is_active?: boolean;
}

export interface InstallationServiceImportResult {
  created: number;
  updated: number;
  errors: string[];
}

// 'standard'/'compleja' retired (CLAUDE.md §7, decided 2026-08-30):
// installation_service_id alone identifies the variant now. 'custom'
// (free-text, no catalog link) is the only value this still carries.
export type TipoInstalacion = 'custom';

// Environment Installation
export interface EnvironmentInstallation {
  id: number;
  environment_id: number;
  installation_service_id?: number;
  tipo_instalacion?: TipoInstalacion;
  cantidad_manual: number;
  trabajo?: string;
  descripcion_trabajo?: string;
  mano_obra_m2?: number;
  unidad?: string;  // Unidad para instalaciones custom: m², ml, global, etc.
  // Sin currency propio todavía (CLAUDE.md §8.5, restaurado 2026-09-01): una
  // línea de catálogo usa installation_service.currency (leída en vivo, sin
  // snapshot acá); una línea 'custom' (sin catálogo) sigue siendo siempre USD.
  // Ambas se muestran convertidas a moneda local con la cotización congelada
  // de la versión cuando corresponde (solo la porción realmente en USD).
  subtotal: number;
  installation_service?: InstallationService;
  materials: EnvironmentInstallationMaterial[];
  created_at: string;
  updated_at?: string;
}

export interface EnvironmentInstallationCreate {
  installation_service_id?: number;
  tipo_instalacion?: TipoInstalacion;
  cantidad_manual?: number;
  trabajo?: string;
  descripcion_trabajo?: string;
  mano_obra_m2?: number;
  unidad?: string;  // Unidad para instalaciones custom
  subtotal?: number;
}

// Environment
export interface Environment {
  id: number;
  budget_version_id: number;
  nombre: string;
  orden: number;
  descuento_materiales?: number;  // % de descuento del ambiente para sus líneas no-outlet (0 = sin tildar)
  materials: EnvironmentMaterial[];
  installation?: EnvironmentInstallation;
  created_at: string;
  updated_at?: string;
  descripcion?: string;
}

export interface EnvironmentCreate {
  nombre: string;
  orden?: number;
  descuento_materiales?: number;
  descripcion?: string;
}

// Freight
export interface Freight {
  id: number;
  budget_version_id: number;
  tipo_envio: string;
  descripcion_detalle?: string;
  monto: number;
  currency: string;
  // CLAUDE.md §6: cuando está tildado, el Flete se cobra $0 al cliente (el
  // PDF imprime "Bonificado" en lugar del precio) pero `monto` conserva el
  // valor real calculado - nunca se pisa/descarta.
  bonificado?: boolean;
  created_at: string;
  updated_at?: string;
  costo?: number;
  tipo?: 'fijo' | 'porcentaje';
  // Link to the FreightRate catalog row this line was built from (CLAUDE.md
  // §8.5, Phase 7a). `codigo` is copied server-side at the moment
  // `freight_rate_id` is set - read-only from the client's perspective.
  freight_rate_id?: number;
  codigo?: string;
}

export interface FreightCreate {
  tipo_envio: string;
  descripcion_detalle?: string;
  monto?: number;
  costo?: number;
  tipo?: 'fijo' | 'porcentaje';
  currency?: string;
  bonificado?: boolean;
  // FK to a FreightRate catalog row (CLAUDE.md §8.5) - undefined/null for a
  // manual entry with no lookup behind it. `codigo` is NOT accepted here:
  // the backend derives it from the referenced rate itself, not a
  // free-form field a caller can set independently.
  freight_rate_id?: number;
}

// --- Comments (CLAUDE.md §10 field report #11, fully specified 2026-08-30) ---
export type CommentScopeType = 'ambiente' | 'mano_de_obra' | 'flete';
// 'internal': visible a usuario + admin, nunca al cliente - nunca imprime en el PDF.
// 'client_visible': imprime en el PDF, debajo/al lado de la sección de su scope.
export type CommentVisibility = 'internal' | 'client_visible';

export interface Comment {
  id: number;
  budget_version_id: number;
  scope_type: CommentScopeType;
  // Requerido (id de ambiente) cuando scope_type es 'ambiente'; debe omitirse
  // para 'mano_de_obra'/'flete' - esas dos secciones son únicas por versión,
  // no hay "cuál" que desambiguar.
  scope_id?: number;
  visibility: CommentVisibility;
  text: string;
  created_by: number;
  created_at: string;
  updated_at?: string;
}

export interface CommentCreate {
  scope_type: CommentScopeType;
  scope_id?: number;
  visibility: CommentVisibility;
  text: string;
}

export interface CommentUpdate {
  visibility?: CommentVisibility;
  text?: string;
}

// Estados de una versión de presupuesto (CLAUDE.md §5).
//
// Espeja exactamente `BudgetVersionState` del backend
// (backend/src/services/budget_state_machine.py), que es la única fuente de
// verdad. 'closed' se renombró a 'sent' ("Enviado") en Phase 6; 'rejected'
// ("No Aprobado", CLAUDE.md §5) reemplaza el viejo send-back a 'draft'.
export type BudgetVersionEstado =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'sent'
  | 'sold'
  | 'lost';

// Pedido de cambio de estado. Es el ÚNICO camino hacia 'sent' y 'sold': el PUT
// de la versión los rechaza para que el chequeo de precios, la fecha de entrega
// y el lock de cotización no sean salteables.
export interface BudgetVersionTransitionRequest {
  target_estado: BudgetVersionEstado;
  fecha_prevista_entrega?: string;   // YYYY-MM-DD
  reason?: string;                   // requerido por el backend si target === 'rejected'
}

export interface StalePriceMismatch {
  kind: 'changed' | 'missing';
  material_id: number;
  environment_id: number;
  codigo?: string;
  descripcion?: string;
  captured_price?: number;
  current_price?: number;
}

// Una SECCIÓN (Materiales / Mano de Obra / Flete) cuyo descuento de sección
// ingresado por el vendedor superó su descuento aprobado para esa sección
// (CLAUDE.md §8.7/§8.11, rediseñado 2026-09-08) - la otra razón (junto a un
// precio desactualizado) por la que un envío puede derivar a
// 'pending_approval'. Las tres secciones se controlan ahora.
export interface DiscountCapViolation {
  section: 'materiales' | 'mano_de_obra' | 'flete';
  section_label: string;
  descuento_pct: number;
  cap_pct: number;
}

export interface StalePriceReport {
  checked_at: string;
  mismatches: StalePriceMismatch[];
}

export interface DiscountCapViolationReport {
  checked_at: string;
  violations: DiscountCapViolation[];
}

// OJO: un 200 no significa "se envió". Si `diverted_to_approval` es true, el
// chequeo de precios (o el de tope de descuento) encontró un problema y la
// versión quedó en 'pending_approval' sin enviarse.
export interface BudgetVersionTransitionResult {
  estado: BudgetVersionEstado;
  requested_estado: BudgetVersionEstado;
  diverted_to_approval: boolean;
  stale_prices: StalePriceMismatch[];
  discount_violations: DiscountCapViolation[];
  spawned_version_id?: number;
  spawned_version_number?: number;
  fx_rate?: number;
  fx_currency?: string;
  fx_source?: string;
  fecha_prevista_entrega?: string;
  // Job de PDF que la propia transición encoló al enviar (solo si `estado`
  // terminó siendo 'sent') - evita una segunda llamada separada de "start"
  // para el mismo PDF que el chequeo de envío ya gatilló.
  pdf_job_id?: string;
}

// Calculadora H: guardar % H corre el mismo chequeo/cola de aprobación que el
// de precios desactualizados de Materiales - un 200 no dice por sí solo si la
// versión quedó donde estaba o fue derivada a 'pending_approval'.
export interface PorcentajeHUpdateResult {
  estado: BudgetVersionEstado;
  diverted_to_approval: boolean;
  porcentaje_h: number;
  porcentaje_h_updated_at?: string;
  h_fx_rate?: number;
  h_fx_currency?: string;
  h_fx_source?: string;
  h_fx_rate_date?: string;
}

// Budget Version
export interface BudgetVersion {
  id: number;
  budget_id: number;
  version_number: number;
  titulo: string;
  vendedor: string;
  email_vendedor: string;
  tel_vendedor: string;
  condiciones_contratacion: string;
  condiciones_adicionales?: string;
  descuento_instalacion?: number;
  descuento_fletes?: number;
  total_materiales: number;
  total_materiales_usd?: number;
  total_materiales_ars?: number;
  total_flete: number;
  total_instalacion: number;
  estado: BudgetVersionEstado;
  // Phase 6 (CLAUDE.md §5/§6)
  price_list_id?: number;
  fecha_prevista_entrega?: string;
  fecha_entrega_confirmada_at?: string;
  // Cotización congelada al enviar. Mostrar Mano de Obra con ESTE número, no
  // con uno en vivo: es el que se imprimió en el PDF del cliente.
  fx_rate?: number;
  fx_currency?: string;
  fx_source?: string;
  fx_rate_date?: string;
  fx_locked_at?: string;
  sent_at?: string;
  spawned_from_version_id?: number;
  pdf_job_id?: string;
  // Etiqueta editable por versión (field report #16), independiente del
  // número de versión inmutable. undefined/null = sin nombre - mostrar
  // "Versión {version_number}" como fallback.
  nombre_version?: string;
  // Mano de Obra separada por moneda (CLAUDE.md §6) - misma idea que
  // total_materiales_usd/ars.
  total_instalacion_usd?: number;
  total_instalacion_ars?: number;
  // Flete antes de excluir las líneas Bonificado (CLAUDE.md §6) - el número
  // interno; `total_flete` es lo que efectivamente ve/paga el cliente.
  total_flete_interno?: number;
  // Calculadora H (CLAUDE.md §5/§6) - % H persistido y su propia cotización,
  // separada de fx_rate/fx_currency/fx_source de arriba (esa es la del envío
  // al cliente y se congela una sola vez; esta es la del calculador, se
  // refresca en la creación de versión y en la venta - ver PorcentajeHUpdateResult).
  porcentaje_h?: number;
  porcentaje_h_updated_at?: string;
  h_fx_rate?: number;
  h_fx_currency?: string;
  h_fx_source?: string;
  h_fx_rate_date?: string;
  h_fx_fetched_at?: string;
  // Motivo(s) persistido(s) de por qué esta versión está/estuvo en
  // 'pending_approval' (CLAUDE.md §5/§8.7) - a diferencia de
  // BudgetVersionTransitionResult.stale_prices/discount_violations (que solo
  // llegan en la respuesta del propio intento de envío), esto se puede leer
  // en cualquier momento, incluso después de recargar la página. undefined/
  // null = nunca derivó por esa razón, o el motivo ya se resolvió (ambos se
  // limpian juntos al aprobar/devolver/enviar con éxito).
  stale_price_report?: StalePriceReport;
  discount_cap_violation_report?: DiscountCapViolationReport;
  // Motivo del rechazo (CLAUDE.md §5); sólo se muestra mientras estado === 'rejected'.
  rejection_reason?: string | null;
  // ERP (CLAUDE.md §8.12): ids de los comprobantes cargados en Bejerman (NP/NPH)
  // y el flag pasivo "No cargado en ERP" cuando un submit falló. Se leen para
  // mostrar el estado de carga y habilitar el reintento manual (admin).
  erp_np_document_id?: string | null;
  erp_nph_document_id?: string | null;
  erp_submission_error?: string | null;
  environments: Environment[];
  freights: Freight[];
  // Lista plana - filtrar por scope_type/scope_id (field report #11). Un
  // comentario 'ambiente' ya trae su scope_id apuntando al ambiente correcto.
  comments?: Comment[];
  created_at: string;
  updated_at?: string;
  // Último guardado del estado completo (CLAUDE.md §10 report #9). undefined/
  // null si nunca se hizo un full-save - mostrar created_at como fallback.
  last_saved_at?: string;
  // Campos adicionales compatibles
  numero_version?: string;
  nombre?: string;
  condiciones?: string;
  fecha_creacion?: string;
  total_final?: number;
  is_current?: boolean;
  items?: any[];
}

// ERP submit (CLAUDE.md §8). El search/create devuelven registros nativos
// crudos de Bejerman (claves Cliente_*), así que se tipan laxos.
export interface ErpClienteCreateRequest {
  razon_social: string;
  cuit: string;
  tipo_documento?: number;
  situacion_iva?: string;
  situacion_iibb?: string;
  situacion_ganancias?: string;
  codigo_provincia?: string;
  vendedor_codigo?: string;
  condicion_venta_codigo?: string;
  definible1_codigo?: string;
}

export interface ErpSubmitResult {
  estado: string;
  ok: boolean;
  nph_required: boolean;
  erp_np_document_id?: string | null;
  erp_nph_document_id?: string | null;
  submitted: { np: boolean; nph: boolean };
  unresolved_skus: string[];
  error?: string | null;
}

// Poll del submit a ERP en background (SUPERVISOR_NOTES 2026-09-15). El submit
// ahora devuelve 202 + handle; el outcome se lee de este endpoint, que refleja
// lo persistido en la versión.
export interface ErpSubmitStatus {
  erp_submission_status: 'none' | 'queued' | 'in_progress' | 'done' | 'failed';
  ok: boolean;
  erp_np_document_id?: string | null;
  erp_nph_document_id?: string | null;
  error?: string | null;
}

export interface BudgetVersionCreate {
  titulo: string;
  vendedor: string;
  email_vendedor: string;
  tel_vendedor: string;
  condiciones_contratacion: string;
  condiciones_adicionales?: string;
  descuento_instalacion?: number;
  descuento_fletes?: number;
  environments?: EnvironmentCreate[];
  freights?: FreightCreate[];
  // Campos adicionales para el formulario
  numero_version?: string;
  nombre?: string;
  condiciones?: string;
  // Etiqueta editable por versión (field report #16) - también aceptado por
  // el PUT liviano /budgets/{id}/versions/{id} (BudgetVersionUpdate en el
  // backend), no solo por el full-save. Ver BudgetVersion.nombre_version.
  nombre_version?: string;
  // Permitir update de estado desde el frontend
  estado?: BudgetVersionEstado;
  // Lista de precios elegida por el rep (CLAUDE.md §4) - opcional, así una
  // versión puede crearse antes de que el rep decida una lista.
  price_list_id?: number;
}

// --- Bulk "save full version" payload (CLAUDE.md §10 field reports #2/#7) ---
// One PUT with the version's ENTIRE nested state, reconciled server-side in a
// single transaction - replaces the old one-request-per-item save sequence.
// `id` omitted/undefined = create; `id` present = update; anything that
// exists server-side but is missing from the submitted list gets deleted.
export interface EnvironmentInstallationMaterialFullSave extends EnvironmentInstallationMaterialCreate {
  id?: number;
}

export interface EnvironmentInstallationFullSave extends EnvironmentInstallationCreate {
  id?: number;
  materials?: EnvironmentInstallationMaterialFullSave[];
}

export interface EnvironmentMaterialFullSave extends EnvironmentMaterialCreate {
  id?: number;
}

export interface EnvironmentFullSave extends EnvironmentCreate {
  id?: number;
  materials?: EnvironmentMaterialFullSave[];
  installations?: EnvironmentInstallationFullSave[];
}

export interface FreightFullSave extends FreightCreate {
  id?: number;
}

export interface BudgetVersionFullSave {
  titulo?: string;
  vendedor?: string;
  email_vendedor?: string;
  tel_vendedor?: string;
  condiciones_contratacion?: string;
  condiciones_adicionales?: string;
  descuento_instalacion?: number;
  descuento_fletes?: number;
  // Lista de precios elegida por el rep (CLAUDE.md §4) - ver BudgetVersion.price_list_id.
  price_list_id?: number;
  // Fecha prevista de entrega (CLAUDE.md §5) - campo editable normal mientras
  // la versión es borrador; el backend ya lo persiste en save_full_version.
  fecha_prevista_entrega?: string;   // YYYY-MM-DD
  nombre_version?: string;
  // % H (no facturado, 0-100). Se guarda con el resto de la versión (CLAUDE.md
  // §5, PO 2026-09-15) - reemplaza el botón "Guardar % H" aparte. El gate H>50%
  // se evalúa al Enviar, no al guardar.
  porcentaje_h?: number;
  environments?: EnvironmentFullSave[];
  freights?: FreightFullSave[];
  // Generated fresh client-side per save attempt (a UUID). A retry that
  // reuses the same token as the last successfully-applied save is a no-op
  // server-side - covers a request that committed but whose response never
  // reached the client (see BudgetService.save_full_version).
  save_token?: string;
}

// --- Config por país (Phase 6, CLAUDE.md §6): IVA % y Flete on/off ---
export interface CountryConfig {
  iva_percentage: number;
  flete_enabled: boolean;
}

export interface CountryConfigUpdate {
  iva_percentage?: number;
  flete_enabled?: boolean;
}

// --- Resumen por sección (Phase 6, CLAUDE.md §6/§10 field report #6) ---
// Fuente única de esta cuenta - "Calculadora" y cualquier export/PDF futuro
// deberían leer esto, no recalcularlo por su cuenta.
export interface FreightSectionLine {
  id: number;
  tipo_envio?: string;
  monto_interno: number;
  monto_cliente: number;
  bonificado: boolean;
  currency: string;
}

export interface VersionSectionSummary {
  // Materiales (CLAUDE.md §8.7 outlet model): Subtotal (suma de ambientes,
  // neto de descuentos por línea) == Total (materiales_usd) + IVA.
  materiales_usd: number;
  materiales_subtotal_usd: number;
  iva_percentage: number;
  materiales_total_con_iva: number;
  descuento_efectivo_materiales: number;
  // Mano de Obra (moneda local): subtotal, descuento de sección, neto.
  mano_obra_subtotal_local: number;
  mano_obra_descuento_pct: number;
  mano_obra_descuento_local: number;
  mano_obra_total_local: number;
  mano_obra_currency: string;
  fx_rate_used?: number;
  // Flete: subtotal (excl. Bonificado), descuento de sección, neto e interno.
  flete_subtotal_cliente: number;
  flete_descuento_pct: number;
  flete_total_cliente: number;
  flete_total_interno: number;
  flete_currency: string;
  flete_habilitado: boolean;
  flete_lines: FreightSectionLine[];
}

// Budget Summary - Para listados (sin versions)
export interface BudgetSummary {
  descuento_instalacion?: number;
  descuento_fletes?: number;
  id: number;
  code: string;
  proyecto: string;
  cliente: string;
  status: BudgetVersionEstado;
  total_materiales?: number;
  total_flete?: number;
  total_instalacion?: number;
  total_amount?: number;
  vendedor?: string;
  current_version_number?: number;
  current_version_titulo?: string;
  current_version_fecha?: string;
  // Último guardado de la versión vigente (CLAUDE.md §10 report #9). undefined/
  // null si nunca se hizo un full-save - mostrar current_version_fecha.
  current_version_last_saved_at?: string;
  current_version_vendedor?: string;
  created_at: string;
  updated_at?: string;
}

// Budget - Esquema completo del backend
export interface Budget {
  id: number;
  code: string;
  proyecto: string;
  cliente: string;
  responsable?: string;
  email_cliente?: string;
  tel_cliente?: string;
  status: BudgetVersionEstado;
  user_id: number;
  versions: BudgetVersion[];
  created_at: string;
  updated_at?: string;
  user: {
    id: number;
    name: string;
    email: string;
  };
}

export interface BudgetCreate {
  proyecto: string;
  cliente: string;
  responsable?: string;
  email_cliente?: string;
  tel_cliente?: string;
}

// Formularios y requests
export interface BudgetFormData {
  proyecto: string;
  cliente: string;
  responsable?: string;
  email_cliente?: string;
  tel_cliente?: string;
}

export interface BudgetFilters {
  status?: string;
  search?: string;
  from_date?: string;
  to_date?: string;
  limit?: number;
  skip?: number;
}

export interface BudgetItem {
  id: number;
  product_id?: number;
  product?: Product;
  metros_cuadrados: number;
  cantidad?: number;
  con_desperdicio: boolean;
  con_estructura: boolean;
  con_terminaciones: boolean;
  con_instalacion: boolean;
  subtotal?: number;
  is_structure?: boolean;
  product_sku?: string;
  product_codigo: string;
  product_color?: string;
  product_grupo?: string;
  product_categoria?: string;
  product_largo?: number;
  product_ancho?: number;
  product_espesor?: number;
  product_estructura_m2?: string;
  product_is_active?: boolean;
  precio_unitario?: number;
}

export interface AddItemRequest {
  product_id: number;
  metros_cuadrados: number;
  con_desperdicio: boolean;
  con_estructura: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Export status types - Backend compatible
export enum BudgetStatus {
  DRAFT = 'draft',
  PENDING_APPROVAL = 'pending_approval',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  SENT = 'sent',
  SOLD = 'sold',
  LOST = 'lost'
}

// Dashboard types
export interface DashboardStats {
  total_budgets: number;
  pending_budgets: number;
  approved_budgets: number;
  total_value: number;
  recent_budgets: Budget[];
}

// Advanced Dashboard types
export interface BudgetStatusCounts {
  draft: number;
  pending_approval: number;
  approved: number;
  rejected: number;
  sent: number;
  sold: number;
  lost: number;
}

export interface AdvancedDashboardStats {
  // Estadísticas básicas
  total_budgets: number;
  budgets_this_month: number;
  total_active_products: number;
  monthly_revenue: number;
  
  // Estadísticas avanzadas
  budgets_by_status: BudgetStatusCounts;
  conversion_rate_to_approved: number;
  conversion_rate_to_sold: number;
  average_budget_value: number;
  pending_budgets: number;
  total_sold_revenue: number;
  total_approved_revenue: number;
  
  // Ventas separadas por moneda
  total_sold_revenue_usd?: number;
  total_sold_revenue_ars?: number;
  total_sold_materials_usd?: number;
  total_sold_materials_ars?: number;
  total_sold_flete_ars?: number;
  total_sold_installation_ars?: number;
}

// User Activity Stats
export interface UserActivityData {
  user_id: number;
  name: string;
  email: string;
  total_budgets: number;
  closed_budgets: number;
  approved_budgets: number;
  conversion_rate: number;
  member_since: string | null;
}

export interface UserActivityStats {
  most_active_users: UserActivityData[];
  best_conversion_users: UserActivityData[];
  total_users: number;
  active_users: number;
  inactive_users: number;
}

// User Sales Stats
export interface UserSalesData {
  user_id: number;
  name: string;
  email: string;
  total_sales_amount: number;
  total_closed_budgets: number;
  contribution_percentage?: number;
}

export interface UserSalesStats {
  top_sellers_by_amount: UserSalesData[];
  top_sellers_by_count: UserSalesData[];
  total_sales_amount: number;
}

// API Response types
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface ErrorResponse {
  detail: string;
}
// Mercado Libre invoicing v1 - one row per ML order (GET /ml/orders).
export interface MlOrder {
  id: number;
  ml_order_id: string;
  pack_id: string | null;
  status: 'received' | 'processing' | 'success' | 'failed' | 'ignored';
  error: string | null;
  buyer_doc_type: string | null;
  buyer_doc_number: string | null;
  razon_social: string | null;
  invoice_type: 'A' | 'B' | null;
  cliente_codigo: string | null;
  total_ars: number | null;
  erp_np_document_id: string | null;
  erp_nph_document_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}
